'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '../lib/supabase/client';

export type UserRole = 'public_user' | 'member' | 'staff' | 'admin' | 'owner';

export interface UserProfile {
  id: string;
  email: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
  role: UserRole;
  gxg_coins: number;
  level: number;
  xp: number;
  tier: string;
  is_active: boolean;
  created_at?: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  role: UserRole | null;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isStaffOrAbove: () => boolean;
  isAdminOrAbove: () => boolean;
  isOwner: () => boolean;
  isMemberOrAbove: () => boolean;
  canAccessAdmin: () => boolean;
}

const ROLE_HIERARCHY: Record<UserRole, number> = {
  public_user: 0,
  member: 1,
  staff: 2,
  admin: 3,
  owner: 4,
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) {
        console.error('Error fetching profile:', error.message);
        return null;
      }
      return data as UserProfile;
    } catch {
      return null;
    }
  }, [supabase]);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    const p = await fetchProfile(user.id);
    setProfile(p);
  }, [user, fetchProfile]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, supabase.auth]);

  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.fullName || '',
          avatar_url: metadata.avatarUrl || '',
          role: 'public_user',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const hasMinRole = (minRole: UserRole): boolean => {
    if (!profile) return false;
    return ROLE_HIERARCHY[profile.role] >= ROLE_HIERARCHY[minRole];
  };

  const isMemberOrAbove = () => hasMinRole('member');
  const isStaffOrAbove = () => hasMinRole('staff');
  const isAdminOrAbove = () => hasMinRole('admin');
  const isOwner = () => profile?.role === 'owner';
  const canAccessAdmin = () => hasMinRole('staff');

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    role: profile?.role ?? null,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    isStaffOrAbove,
    isAdminOrAbove,
    isOwner,
    isMemberOrAbove,
    canAccessAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
